/**
 * 通知提示音 - 收到 SSE 实时通知时播放
 *
 * 使用 Web Audio API 合成一段短促的双音"叮"声，无需引入音频资源文件。
 * AudioContext 惰性创建，若被浏览器自动播放策略挂起则尝试 resume；
 * 任何异常均静默忽略，避免影响通知主流程。
 */

type WebkitWindow = Window & { webkitAudioContext?: typeof AudioContext };

let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const AudioCtor =
    window.AudioContext || (window as WebkitWindow).webkitAudioContext;
  if (!AudioCtor) {
    return null;
  }
  if (!audioCtx) {
    audioCtx = new AudioCtor();
  }
  return audioCtx;
}

/**
 * 播放通知提示音（双音短鸣）
 */
export function playNotificationSound(): void {
  try {
    const ctx = getAudioContext();
    if (!ctx) {
      return;
    }
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {
        /* 忽略 resume 失败（无用户手势时） */
      });
    }

    const now = ctx.currentTime;
    const notes = [
      { freq: 880, start: 0, duration: 0.15 },
      { freq: 1320, start: 0.15, duration: 0.2 },
    ];

    notes.forEach(({ freq, start, duration }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      osc.connect(gain);
      gain.connect(ctx.destination);

      const t0 = now + start;
      const t1 = t0 + duration;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.3, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t1);
      osc.start(t0);
      osc.stop(t1 + 0.02);
    });
  } catch {
    /* 忽略音频异常（自动播放策略等） */
  }
}

export default playNotificationSound;
